import React from 'react';
import { Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import './App.css';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import AuthLayout from './components/Layout/AuthLayout';
import ProtectedRoute from './components/ProtectedRoute';
import StorageClearButton from './components/StorageClearButton/StorageClearButton';
import { WorkReportWidget } from './components/WorkReportWidget';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';
import BlogCategories from './pages/Admin/BlogCategories';
import BlogEditor from './pages/Admin/BlogEditor';
import BlogManagement from './pages/Admin/BlogManagement';
import NewsroomPlanning from './pages/Admin/NewsroomPlanning';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import AllCandidates from './pages/AllCandidates';
import AutonomousCampaign from './pages/AutonomousCampaign';
import CampaignRefinement from './pages/CampaignRefinement';
import CampaignPlanReview from './pages/CampaignPlanReview';
import CampaignProgress from './pages/CampaignProgress';
import CampaignResults from './pages/CampaignResults';
import CampaignCandidates from './pages/CampaignCandidates';
import CampaignList from './pages/CampaignList';
import CardTest from './components/metronic/test/CardTest';
import Billing from './pages/Billing';
import BlogPost from './pages/BlogPost';
import Candidates from './pages/Candidates';
import CandidateSearchPage from './pages/CandidateSearchPage';
import Community from './pages/Community';
import Contacts from './pages/Contacts';
import Conversation from './pages/Conversation';
import DashboardNew from './pages/DashboardNew';
import DataEnrichment from './pages/DataEnrichment';
import CandidateLists from './pages/CandidateLists';
import Landing from './pages/Landing';
import { ForCandidatesPage, ForEmployersPage } from './pages/AudienceLandingPages';
import Login from './pages/Login';
import MarketIntelligence from './pages/MarketIntelligence';
import MarketIntelligenceHub from './pages/MarketIntelligenceHub';
import MarketIntelligenceArticle from './pages/MarketIntelligenceArticle';
import MarketIntelligenceSection from './pages/MarketIntelligenceSection';
import MIEIntelligence from './pages/MIEIntelligence';
import CandidateIntelligenceProfileBuilder from './pages/CandidateIntelligenceProfileBuilder';
import PeopleSearch from './pages/PeopleSearch';
import QRJobLanding from './pages/QRJobLanding';
import Settings from './pages/Settings';
import Signup from './pages/Signup';
import WorkReportTest from './pages/WorkReportTest';
import CandidateStoryPage from './pages/CandidateStoryPage';
import { peopleSearchService } from './services/peopleSearchService';
import AppLayout from './metronic/layouts/AppLayout';

/** Public marketing/auth routes never show the internal work-report agent. */
const WORK_REPORT_WIDGET_HIDDEN_EXACT = new Set([
  '/',
  '/employers',
  '/talent',
  '/candidates',
  '/community',
  '/login',
  '/signup',
  '/admin/login',
]);

function pathShouldHideWorkReportWidget(pathname) {
  if (WORK_REPORT_WIDGET_HIDDEN_EXACT.has(pathname)) return true;
  if (pathname.startsWith('/market-intelligence')) return true;
  if (pathname.startsWith('/blog/')) return true;
  if (pathname.startsWith('/q/')) return true;
  return false;
}

// Widget only on app pages: authenticated + not on landing/login/signup (etc.)
const AuthenticatedWorkReportWidget = () => {
  const { currentUser, loading } = useAuth();
  const { pathname } = useLocation();

  if (loading || !currentUser) return null;
  if (pathShouldHideWorkReportWidget(pathname)) return null;

  return <WorkReportWidget />;
};

function App() {
  // Handler functions for PeopleSearch component
  const handlePeopleSearch = async (query) => {
    try {
      return await peopleSearchService.searchProfiles(query);
    } catch (error) {
      console.error('Error searching profiles:', error);
      return [];
    }
  };

  const handleProfileEvaluation = async (profile) => {
    try {
      return await peopleSearchService.evaluateProfile(profile);
    } catch (error) {
      console.error('Error evaluating profile:', error);
      return null;
    }
  };

  const handleProfileAction = async (action, profile) => {
    try {
      return await peopleSearchService.performAction(action, profile);
    } catch (error) {
      console.error('Error performing action:', error);
      return null;
    }
  };

  return (
    <AuthProvider>
      <Router>
        <StorageClearButton />
        <AuthenticatedWorkReportWidget />
        <Routes>
          {/* Public Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
          </Route>

          {/* Public Routes */}
          <Route path="/employers" element={<ForEmployersPage />} />
          <Route path="/talent" element={<ForCandidatesPage />} />
          <Route path="/candidates" element={<Candidates />} />
          <Route path="/community" element={<Community />} />
          <Route path="/q/:token" element={<QRJobLanding />} />

          {/* Candidate Search Route - Protected */}
          <Route path="/candidate-search" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CandidateSearchPage />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          {/* Protected Main App Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <SidebarProvider>
                <AppLayout>
                  <DashboardNew />
                </AppLayout>
              </SidebarProvider>
            </ProtectedRoute>
          } />

          <Route path="/conversation" element={
            <ProtectedRoute>
              <SidebarProvider>
                <Conversation />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          <Route path="/talent-insights" element={
            <ProtectedRoute>
              <SidebarProvider>
                <AppLayout>
                  <MarketIntelligence />
                </AppLayout>
              </SidebarProvider>
            </ProtectedRoute>
          } />

          <Route path="/market-intelligence" element={<MarketIntelligenceHub />} />
          <Route path="/market-intelligence/archive" element={<MarketIntelligenceSection mode="archive" />} />
          <Route path="/market-intelligence/search" element={<MarketIntelligenceSection mode="search" />} />
          <Route path="/market-intelligence/subscribe" element={<MarketIntelligenceSection mode="subscribe" />} />
          <Route path="/market-intelligence/briefing" element={<MarketIntelligenceSection mode="briefing" />} />
          <Route path="/market-intelligence/category/:value" element={<MarketIntelligenceSection mode="filter" filterKey="category" />} />
          <Route path="/market-intelligence/practice/:value" element={<MarketIntelligenceSection mode="filter" filterKey="practice" />} />
          <Route path="/market-intelligence/vertical/:value" element={<MarketIntelligenceSection mode="filter" filterKey="vertical" />} />
          <Route path="/market-intelligence/topic/:value" element={<MarketIntelligenceSection mode="filter" filterKey="topic" />} />
          <Route path="/market-intelligence/function/:value" element={<MarketIntelligenceSection mode="filter" filterKey="function" />} />
          <Route path="/market-intelligence/sector/:value" element={<MarketIntelligenceSection mode="filter" filterKey="sector" />} />
          <Route path="/market-intelligence/region/:value" element={<MarketIntelligenceSection mode="filter" filterKey="region" />} />
          <Route path="/market-intelligence/author/:value" element={<MarketIntelligenceSection mode="filter" filterKey="author" />} />
          <Route path="/market-intelligence/:slug" element={<MarketIntelligenceArticle />} />

          <Route path="/mie-intelligence" element={
            <ProtectedRoute>
              <SidebarProvider>
                <AppLayout>
                  <MIEIntelligence />
                </AppLayout>
              </SidebarProvider>
            </ProtectedRoute>
          } />

          <Route path="/candidate-profile-builder" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CandidateIntelligenceProfileBuilder />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          <Route path="/settings" element={
            <ProtectedRoute>
              <SidebarProvider>
                <Settings />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          <Route path="/billing" element={
            <ProtectedRoute>
              <SidebarProvider>
                <Billing />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          {/* Autonomous Campaign Routes */}
          <Route path="/campaigns/autonomous" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CampaignList />
              </SidebarProvider>
            </ProtectedRoute>
          } />
          <Route path="/campaigns/autonomous/new" element={
            <ProtectedRoute>
              <SidebarProvider>
                <AutonomousCampaign />
              </SidebarProvider>
            </ProtectedRoute>
          } />
          <Route path="/campaigns/autonomous/:session_id/refine" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CampaignRefinement />
              </SidebarProvider>
            </ProtectedRoute>
          } />
          <Route path="/campaigns/autonomous/:session_id/plan" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CampaignPlanReview />
              </SidebarProvider>
            </ProtectedRoute>
          } />
          <Route path="/campaigns/autonomous/:session_id/progress" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CampaignProgress />
              </SidebarProvider>
            </ProtectedRoute>
          } />
          <Route path="/campaigns/autonomous/:session_id/results" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CampaignResults />
              </SidebarProvider>
            </ProtectedRoute>
          } />
          <Route path="/campaigns/autonomous/:session_id/candidates" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CampaignCandidates />
              </SidebarProvider>
            </ProtectedRoute>
          } />
          {/* Preview routes for design viewing */}
          <Route path="/campaigns/autonomous/new/CampaignRefinement" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CampaignRefinement previewMode={true} />
              </SidebarProvider>
            </ProtectedRoute>
          } />
          <Route path="/campaigns/autonomous/new/CampaignPlanReview" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CampaignPlanReview previewMode={true} />
              </SidebarProvider>
            </ProtectedRoute>
          } />
          <Route path="/campaigns/autonomous/new/CampaignProgress" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CampaignProgress previewMode={true} />
              </SidebarProvider>
            </ProtectedRoute>
          } />
          <Route path="/campaigns/autonomous/new/CampaignResults" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CampaignResults previewMode={true} />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          {/* Legacy Routes - Keep for now */}
          {/* Campaign and Assistant routes hidden - under development */}
          {/* <Route path="/campaign" element={
            <ProtectedRoute>
              <SidebarProvider>
                <Campaign />
              </SidebarProvider>
            </ProtectedRoute>
          } /> */}

          <Route path="/contacts" element={
            <ProtectedRoute>
              <SidebarProvider>
                <Contacts />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          {/* <Route path="/assistant" element={
            <ProtectedRoute>
              <SidebarProvider>
                <Assistant />
              </SidebarProvider>
            </ProtectedRoute>
          } /> */}

          <Route path="/data-enrichment" element={
            <ProtectedRoute>
              <SidebarProvider>
                <DataEnrichment />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          <Route path="/people-search" element={
            <ProtectedRoute>
              <SidebarProvider>
                <PeopleSearch
                  onSearch={handlePeopleSearch}
                  onEvaluate={handleProfileEvaluation}
                  onAction={handleProfileAction}
                />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          <Route path="/all-candidates" element={
            <ProtectedRoute>
              <SidebarProvider>
                <AllCandidates />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          <Route path="/candidate-lists" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CandidateLists />
              </SidebarProvider>
            </ProtectedRoute>
          } />
          <Route path="/candidate-lists/:listId" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CandidateLists />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          <Route path="/candidate-story" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CandidateStoryPage />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          {/* Work Report Agent Test Route */}
          <Route path="/work-report-test" element={
            <ProtectedRoute>
              <WorkReportTest />
            </ProtectedRoute>
          } />

          {/* Metronic Components Test Route */}
          <Route path="/metronic-test" element={
            <ProtectedRoute>
              <SidebarProvider>
                <CardTest />
              </SidebarProvider>
            </ProtectedRoute>
          } />

          {/* Admin Routes - Separate from User Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={
            <AdminProtectedRoute>
              <AdminDashboard />
            </AdminProtectedRoute>
          } />

          {/* Blog Management Routes (Admin) */}
          <Route path="/admin/blogs" element={
            <AdminProtectedRoute>
              <BlogManagement />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/market-intelligence" element={
            <AdminProtectedRoute>
              <BlogManagement />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/newsroom" element={
            <AdminProtectedRoute>
              <NewsroomPlanning />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/blogs/new" element={
            <AdminProtectedRoute>
              <BlogEditor />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/market-intelligence/new" element={
            <AdminProtectedRoute>
              <BlogEditor />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/blogs/:id/edit" element={
            <AdminProtectedRoute>
              <BlogEditor />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/market-intelligence/:id/edit" element={
            <AdminProtectedRoute>
              <BlogEditor />
            </AdminProtectedRoute>
          } />
          <Route path="/admin/categories" element={
            <AdminProtectedRoute>
              <BlogCategories />
            </AdminProtectedRoute>
          } />

          {/* Public Blog Routes */}
          <Route path="/blog/:id" element={<BlogPost />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
